import { ReportPeriodControlComponent } from '../../../components/report-period-control/report-period-control.component';
import {
  ReportDataAvailabilityService,
  availabilityForReport,
} from '../../../services/report-data-availability.service';
import {
  ReportPeriodSelection,
  isPaidReport,
  reportRangeErrors,
  resolveReportPeriod,
  selectReportPeriod,
} from '../../../utils/report-period.utils';
import { getReportVariableHelpText } from '../../../utils/report-variable-help-text';
import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DxTooltipModule, DxTemplateModule, DxFormComponent, DxFormModule, DxNumberBoxComponent, DxTextBoxComponent, DxValidatorModule } from 'devextreme-angular';
import { EditorOptions, Messages } from 'ag-common-lib/lib/constants/validation.constants';
import { type ReportId, ReportIds, ReportInputParameterKeys } from 'ag-common-lib/lib/contracts/reporting-api.contracts';
import { buildReportVariables } from '../../../components/production-report-viewer-page/reports-core/production-reports.config.shared';
import { ModalWindowComponent } from '../../../components/modal-window/modal-window.component';
import { ModalWindowModule } from '../../../components/modal-window/modal-window.module';
import { ReportDefaultSettingsFormService } from './report-default-settings-form.service';
import { ReportDefaultSettingsFormData, ReportDefaultSettingsFormKeys } from './report-default-settings-form.model';

/**
 * Opens the modal against something other than a user's stored report defaults.
 *
 * Passing `value` puts the modal in CONTROLLED mode: it seeds the form from that
 * value instead of reading Firestore, and on save emits `settingsSaved` instead
 * of writing there — so a caller that owns the settings itself (a bulletin
 * session, say) can reuse the same form without inheriting the storage.
 */
export interface ReportDefaultSettingsModalOptions {
  /** Overrides the built-in `"<report>" Default Settings` heading. */
  caption?: string;
  value?: Partial<ReportDefaultSettingsFormData>;
  /** Fields the caller owns elsewhere and does not want editable here. */
  hiddenKeys?: ReportDefaultSettingsFormKeys[];
}

const REPORT_TITLES: Record<ReportId, string> = {
  [ReportIds.mgaSubmit]: 'MGA Leaders Bulletin - Submitted',
  [ReportIds.mgaPaid]: 'MGA Leaders Bulletin - Paid',
  [ReportIds.personalSubmit]: 'Personal Leaders Bulletin - Submitted',
  [ReportIds.personalPaid]: 'Personal Leaders Bulletin - Paid',
  [ReportIds.rmdSubmit]: 'RMD Leaders Bulletin - Submitted',
  [ReportIds.rmdPaid]: 'RMD Leaders Bulletin - Paid',
};

@Component({
  selector: 'ag-shr-report-default-settings-modal',
  standalone: true,
  imports: [DxTooltipModule, DxTemplateModule, ModalWindowModule, DxFormModule, DxValidatorModule, ReportPeriodControlComponent],
  providers: [ReportDefaultSettingsFormService],
  templateUrl: './report-default-settings-modal.component.html',
  styleUrl: './report-default-settings-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportDefaultSettingsModalComponent {
  @ViewChild('settingsModalRef', { static: true }) private settingsModalRef?: ModalWindowComponent;
  @ViewChild('settingsFormRef') private settingsFormRef?: DxFormComponent;

  /** Emitted instead of saving to Firestore when the modal is opened controlled. */
  @Output() settingsSaved = new EventEmitter<ReportDefaultSettingsFormData>();

  private readonly reportDefaultSettingsFormService = inject(ReportDefaultSettingsFormService);

  /** True while the caller owns the value; see ReportDefaultSettingsModalOptions. */
  protected isControlled = false;
  private readonly availabilityService = inject(ReportDataAvailabilityService);
  protected readonly periodSelectionSig = signal<ReportPeriodSelection>(selectReportPeriod(false, 'custom'));
  protected readonly dataAvailableThroughSig = signal<string | null>(null);
  protected readonly dataAvailableFromSig = signal<string | null>(null);
  protected readonly paidSig = signal(false);
  protected readonly periodInvalidSig = computed(
    () => !this.isControlled && reportRangeErrors(this.periodSelectionSig(), this.dataAvailableThroughSig()).length > 0,
  );
  protected readonly hiddenKeysSig = signal<Set<ReportDefaultSettingsFormKeys>>(new Set());

  private readonly reportIdSig = signal<ReportId | null>(null);
  protected readonly ntpVariableSig = computed(() => {
    const reportId = this.reportIdSig();
    return reportId ? buildReportVariables(reportId).find(variable => variable.name === ReportInputParameterKeys.ntpDisplayMode) : undefined;
  });
  protected readonly isPersonalSig = computed(() =>
    this.reportIdSig() === ReportIds.personalPaid || this.reportIdSig() === ReportIds.personalSubmit,
  );
  protected readonly ntpEditorOptionsSig = computed(() => ({
    items: this.ntpVariableSig()?.options ?? [],
    valueExpr: 'value',
    displayExpr: 'label',
    showClearButton: false,
    labelMode: 'hidden',
  }));
  protected readonly getVariableHelpText = getReportVariableHelpText;
  protected readonly yesNoEditorOptions = { switchedOffText: 'No', switchedOnText: 'Yes' };

  protected caption = '';
  protected readonly formDataSig = this.reportDefaultSettingsFormService.formDataSig;
  protected readonly isLoadingSig = toSignal(this.reportDefaultSettingsFormService.isDataLoading$, {
    initialValue: false,
  });
  protected readonly inProgressSig = toSignal(this.reportDefaultSettingsFormService.inProgress$, {
    initialValue: false,
  });
  protected readonly handleClosePopup = this.reportDefaultSettingsFormService.onCancelEdit;
  protected readonly Messages = Messages;
  protected readonly ReportDefaultSettingsFormKeys = ReportDefaultSettingsFormKeys;

  protected readonly titleEditorOptions = {
    labelMode: 'static',
    valueChangeEvent: 'input',
    placeholder: 'Default',
  };

  protected readonly subtitleEditorOptions = {
    labelMode: 'static',
    valueChangeEvent: 'input',
    placeholder: 'Default',
  };

  /** Disabled rather than hidden while the switch is off, so the switch keeps its place on the row. */
  protected readonly displayThresholdEditorOptionsSig = computed(() => ({
    ...EditorOptions.CURRENCY,
    showSpinButtons: true,
    disabled: this.formDataSig()?.[ReportDefaultSettingsFormKeys.includeThreshold] === false,
  }));

  /** Keeps the threshold and its switch on one row even where dx-form would stack columns. */
  protected readonly thresholdRowColCountByScreen = { xs: 6, sm: 6 };

  protected readonly includeThresholdEditorOptions = {
    switchedOffText: 'Off',
    switchedOnText: 'On',
    hint: 'Turn off to list every result, whatever the threshold',
  };

  protected readonly rowsPerPageEditorOptions = {
    min: 1,
    showSpinButtons: true,
    format: '#,##0',
  };

  showModal = async (reportId: ReportId, options?: ReportDefaultSettingsModalOptions): Promise<void> => {
    this.reportIdSig.set(reportId);
    this.reportDefaultSettingsFormService.setReportId(reportId);
    this.caption = options?.caption ?? `"${REPORT_TITLES[reportId]}" Default Settings`;
    this.hiddenKeysSig.set(new Set(options?.hiddenKeys ?? []));
    this.isControlled = !!options?.value;

    if (this.isControlled) {
      this.reportDefaultSettingsFormService.getFormData(options?.value);
    } else {
      const hasSavedPeriod = await this.reportDefaultSettingsFormService.loadSettings();
      const { through, from } = availabilityForReport(await this.availabilityService.resolve(), reportId);
      this.dataAvailableThroughSig.set(through);
      this.dataAvailableFromSig.set(from);
      this.paidSig.set(isPaidReport(reportId));
      const data = this.formDataSig();
      this.periodSelectionSig.set(
        resolveReportPeriod(
          isPaidReport(reportId),
          through,
          {
            period: hasSavedPeriod ? data.period : undefined,
            startDate: hasSavedPeriod && data.period === 'custom' ? data.start_date : undefined,
            endDate: hasSavedPeriod && data.period === 'custom' ? data.end_date : undefined,
          },
          from,
        ),
      );
    }

    this.settingsModalRef?.showModal();
  };

  protected isVisibleKey(key: ReportDefaultSettingsFormKeys): boolean {
    return !this.hiddenKeysSig().has(key);
  }

  protected onModalHidden(): void {
    this.isControlled = false;
    this.reportIdSig.set(null);
    this.hiddenKeysSig.set(new Set());
    this.reportDefaultSettingsFormService.clearReportId();
    this.reportDefaultSettingsFormService.getFormData();
  }

  protected onPeriodSelectionChange(selection: ReportPeriodSelection): void {
    this.periodSelectionSig.set(selection);
    const data = this.formDataSig();
    data.period = selection.period;
    data.start_date = selection.period === 'custom' ? selection.startDate : '';
    data.end_date = selection.period === 'custom' ? selection.endDate : '';
  }

  protected onSave = async (): Promise<void> => {
    if (!this.isControlled) {
      if (this.periodInvalidSig()) return;
      const selection = this.periodSelectionSig();
      const data = this.formDataSig();
      data.period = selection.period;
      data.start_date = selection.period === 'custom' ? selection.startDate : '';
      data.end_date = selection.period === 'custom' ? selection.endDate : '';
    }
    const validationResult = this.settingsFormRef?.instance.validate();
    if (validationResult && !validationResult.isValid) {
      return;
    }

    // Controlled: the caller stores this itself, so emit rather than writing to
    // the signed-in user's report defaults.
    if (this.isControlled) {
      this.settingsSaved.emit({ ...this.formDataSig() });
      this.settingsModalRef?.hideModal();

      return;
    }

    const isSaved = await this.reportDefaultSettingsFormService.handleSave();
    if (!isSaved) {
      return;
    }

    this.settingsModalRef?.hideModal();
  };
}
